import React, { useState } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { ArrowLeftIcon, UserIcon, MailIcon, BuildingIcon, CalendarIcon, ClockIcon, ActivityIcon, CheckCircle2Icon, MapPinIcon, PhoneIcon, GlobeIcon, LinkedinIcon, SaveIcon, ShieldIcon, BellIcon, EyeIcon, TrendingUpIcon, UserCheckIcon, CheckIcon, MicIcon, ShoppingCartIcon, MessageCircleIcon, XIcon } from 'lucide-react';
import { AdminUser, PermissionProfile } from '../../../types/users';
import { sessions } from '../../../data/sessions';
interface UserProfilePageProps {
  user: AdminUser;
  onBack: () => void;
  onSave: (updatedUser: Partial<AdminUser>) => void;
}
type TabType = 'overview' | 'profile' | 'speaker-profile' | 'buyer-profile' | 'meetings' | 'availability';
// Mock recent activity
const mockRecentActivity = [{
  id: '1',
  action: 'Logged into mobile app',
  time: '2 hours ago',
  device: 'iPhone 14 Pro'
}, {
  id: '2',
  action: 'Updated profile information',
  time: '1 day ago',
  device: 'Web Browser'
}, {
  id: '3',
  action: 'Booked session: "Nutrition Workshop"',
  time: '2 days ago',
  device: 'Mobile App'
}, {
  id: '4',
  action: 'Connected with 3 exhibitors',
  time: '3 days ago',
  device: 'Mobile App'
}];
export function UserProfilePage({
  user,
  onBack,
  onSave
}: UserProfilePageProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  // Form state for User Profile tab
  const [firstName, setFirstName] = useState(user.name.split(' ')[0] || '');
  const [lastName, setLastName] = useState(user.name.split(' ')[1] || '');
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState(user.role || '');
  const [company, setCompany] = useState(user.companyName || '');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  // Preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);
  const [selectedMeetingDate, setSelectedMeetingDate] = useState<string>('October 24, 2025');
  const [selectedMeetingSlot, setSelectedMeetingSlot] = useState<string | null>(null);
  const [selectedMeetingDetails, setSelectedMeetingDetails] = useState<(typeof mockMeetings)[0] | null>(null);
  // Availability settings state
  const [availabilityByDate, setAvailabilityByDate] = useState<Record<string, string[]>>({
    'October 24, 2025': ['09:00', '09:30', '10:00', '10:30', '11:00', '14:00', '14:30', '15:00'],
    'October 25, 2025': ['10:00', '10:30', '11:00', '13:00', '13:30', '14:00'],
    'October 26, 2025': ['09:00', '09:30', '10:00', '15:00', '15:30', '16:00']
  });
  const [meetingPreference, setMeetingPreference] = useState<'direct' | 'chat-first'>('direct');
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState<string>('October 24, 2025');
  const eventDates = ['October 24, 2025', 'October 25, 2025', 'October 26, 2025'];
  // Generate time slots (30-min intervals)
  const timeSlots: string[] = [];
  for (let hour = 9; hour < 18; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  // Mock user meetings
  const mockMeetings = [{
    id: 'meeting-1',
    attendeeName: 'John Smith',
    attendeeRole: 'Sales Director',
    attendeeCompany: 'FitTech Solutions',
    date: 'October 24, 2025',
    startTime: '10:00',
    endTime: '10:30',
    location: 'FitTech Solutions Stand',
    status: 'confirmed' as const
  }, {
    id: 'meeting-2',
    attendeeName: 'Emma Wilson',
    attendeeRole: 'Marketing Manager',
    attendeeCompany: 'Wellness Corp',
    date: 'October 24, 2025',
    startTime: '14:00',
    endTime: '14:30',
    location: 'Wellness Corp Stand',
    status: 'pending' as const
  }, {
    id: 'meeting-3',
    attendeeName: 'Michael Brown',
    attendeeRole: 'Product Manager',
    attendeeCompany: 'HealthGear Inc',
    date: 'October 25, 2025',
    startTime: '11:00',
    endTime: '11:30',
    location: 'My Company Stand',
    status: 'confirmed' as const
  }];
  // Get meetings for selected date
  const getMeetingsForDate = (date: string) => {
    return mockMeetings.filter(m => m.date === date);
  };
  // Check if a time slot has a meeting
  const getSlotMeeting = (date: string, slot: string) => {
    return mockMeetings.find(m => m.date === date && m.startTime === slot);
  };
  // Check if slot is available
  const isSlotAvailable = (date: string, slot: string) => {
    const meeting = getSlotMeeting(date, slot);
    return !meeting;
  };
  const handleSaveProfile = () => {
    onSave({
      name: `${firstName} ${lastName}`,
      email,
      role: jobTitle,
      companyName: company
    });
    console.log('Profile saved');
  };
  const getPermissionLabel = (permission: PermissionProfile) => {
    switch (permission) {
      case 'general-public':
        return 'General Public';
      case 'b2b-access':
        return 'B2B Access';
      case 'buyer':
        return 'Buyer';
      case 'speaker':
        return 'Speaker';
    }
  };
  const getPermissionBadgeVariant = (permission: PermissionProfile) => {
    switch (permission) {
      case 'general-public':
        return 'default';
      case 'b2b-access':
        return 'success';
      case 'buyer':
        return 'warning';
      case 'speaker':
        return 'error';
    }
  };
  const getPermissionDescription = (permission: PermissionProfile) => {
    switch (permission) {
      case 'general-public':
        return 'Access to public features including event schedules, exhibitor directory, and general information.';
      case 'b2b-access':
        return 'Full B2B access including networking tools, meeting scheduler, private exhibitor areas, and business resources.';
      case 'buyer':
        return 'Buyer access including specialized buyer tools, procurement resources, supplier discovery, and business networking features.';
      case 'speaker':
        return 'Speaker access including session management, presentation tools, speaker resources, and audience engagement features.';
    }
  };
  const getRegistrationSourceLabel = () => {
    return user.registrationSource === 'self-registered' ? 'Self Registered' : 'Invited';
  };
  // Calculate profile completion
  const calculateProfileCompletion = () => {
    let completed = 0;
    let total = 5;
    if (user.name) completed++;
    if (user.email) completed++;
    if (user.role) completed++;
    if (user.companyName) completed++;
    if (user.bio) completed++;
    return Math.round(completed / total * 100);
  };
  const calculateSpeakerProfileCompletion = () => {
    // Mock calculation - in real app would check actual speaker profile fields
    return 45; // Mock: 45% complete
  };
  const calculateBuyerProfileCompletion = () => {
    // Mock calculation - in real app would check actual buyer profile fields
    return 60; // Mock: 60% complete
  };
  // Get speaker sessions
  const getSpeakerSessions = () => {
    // Mock sessions for Sarah Smith for demo purposes
    if (user.name === 'Sarah Smith') {
      return [{
        id: 'mock-1',
        title: 'Women in Fitness Leadership Panel',
        description: 'Panel discussion on leadership opportunities for women in the fitness industry',
        date: 'October 25, 2025',
        startTime: '14:00',
        endTime: '15:00',
        location: 'Dubai Active Talks Stage',
        feature: 'dubai-active-talks',
        category: 'Business',
        image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=500&h=500&fit=crop'
      }, {
        id: 'mock-2',
        title: 'Building a Sustainable Fitness Business',
        description: 'Strategies for creating long-term success in the fitness industry',
        date: 'October 26, 2025',
        startTime: '11:00',
        endTime: '12:00',
        location: 'CEO Forum',
        feature: 'me-fitness',
        category: 'Business',
        image: 'https://images.unsplash.com/photo-1552581234-26160f608093?w=500&h=500&fit=crop'
      }];
    }
    return sessions.filter(session => {
      // Check if user is listed as main speaker
      if (session.speaker === user.name) return true;
      // Check if user is in speakers array
      if (session.speakers && Array.isArray(session.speakers)) {
        return session.speakers.some(s => s.name === user.name);
      }
      return false;
    });
  };
  const speakerSessions = user.permissions.includes('speaker') ? getSpeakerSessions() : [];
  return <div className="min-h-screen bg-gray-50 -m-8" data-id="element-2293">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6" data-id="element-2294">
        <div className="flex items-center justify-between" data-id="element-2295">
          <div className="flex items-center gap-4" data-id="element-2296">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2297">
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-2298" />
            </button>
            <div data-id="element-2299">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-2300">{user.name}</h1>
              <p className="text-sm text-gray-500" data-id="element-2301">{user.email}</p>
            </div>
          </div>

          {/* Progress Indicators */}
          <div className="flex items-center gap-6" data-id="element-2302">
            {/* User Profile Progress */}
            <div className="flex items-center gap-3" data-id="element-2303">
              <div className="text-right" data-id="element-2304">
                <div className="text-xs text-gray-500 mb-1" data-id="element-2305">User Profile</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-2306">
                  {calculateProfileCompletion()}%
                </div>
              </div>
              <div className="relative w-12 h-12" data-id="element-2307">
                <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48" data-id="element-2308">
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#E5E7EB" strokeWidth="4" data-id="element-2309" />
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#3B82F6" strokeWidth="4" strokeDasharray={`${2 * Math.PI * 20}`} strokeDashoffset={`${2 * Math.PI * 20 * (1 - calculateProfileCompletion() / 100)}`} strokeLinecap="round" className="transition-all duration-300" data-id="element-2310" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-2311">
                  <UserIcon className="w-5 h-5 text-blue-600" data-id="element-2312" />
                </div>
              </div>
            </div>

            {/* Speaker Profile Progress */}
            {user.permissions.includes('speaker') && <div className="flex items-center gap-3" data-id="element-2313">
                <div className="text-right" data-id="element-2314">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-2315">Speaker</div>
                  <div className="text-lg font-bold text-gray-900" data-id="element-2316">
                    {calculateSpeakerProfileCompletion()}%
                  </div>
                </div>
                <div className="relative w-12 h-12" data-id="element-2317">
                  <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48" data-id="element-2318">
                    <circle cx="24" cy="24" r="20" fill="none" stroke="#E5E7EB" strokeWidth="4" data-id="element-2319" />
                    <circle cx="24" cy="24" r="20" fill="none" stroke="#F97316" strokeWidth="4" strokeDasharray={`${2 * Math.PI * 20}`} strokeDashoffset={`${2 * Math.PI * 20 * (1 - calculateSpeakerProfileCompletion() / 100)}`} strokeLinecap="round" className="transition-all duration-300" data-id="element-2320" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-2321">
                    <MicIcon className="w-5 h-5 text-orange-600" data-id="element-2322" />
                  </div>
                </div>
              </div>}

            {/* Buyer Profile Progress */}
            {user.permissions.includes('buyer') && <div className="flex items-center gap-3" data-id="element-2323">
                <div className="text-right" data-id="element-2324">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-2325">Buyer</div>
                  <div className="text-lg font-bold text-gray-900" data-id="element-2326">
                    {calculateBuyerProfileCompletion()}%
                  </div>
                </div>
                <div className="relative w-12 h-12" data-id="element-2327">
                  <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48" data-id="element-2328">
                    <circle cx="24" cy="24" r="20" fill="none" stroke="#E5E7EB" strokeWidth="4" data-id="element-2329" />
                    <circle cx="24" cy="24" r="20" fill="none" stroke="#9333EA" strokeWidth="4" strokeDasharray={`${2 * Math.PI * 20}`} strokeDashoffset={`${2 * Math.PI * 20 * (1 - calculateBuyerProfileCompletion() / 100)}`} strokeLinecap="round" className="transition-all duration-300" data-id="element-2330" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-2331">
                    <ShoppingCartIcon className="w-5 h-5 text-purple-600" data-id="element-2332" />
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-10" data-id="element-2333">
        <div className="px-8" data-id="element-2334">
          <div className="flex gap-8" data-id="element-2335">
            <button onClick={() => setActiveTab('overview')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2336">
              Overview
            </button>
            <button onClick={() => setActiveTab('profile')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'profile' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2337">
              User Profile
            </button>
            {user.permissions.includes('speaker') && <button onClick={() => setActiveTab('speaker-profile')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'speaker-profile' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2338">
                Speaker Profile
              </button>}
            {user.permissions.includes('buyer') && <button onClick={() => setActiveTab('buyer-profile')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'buyer-profile' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2339">
                Buyer Profile
              </button>}
            {user.permissions.includes('b2b-access') && <button onClick={() => setActiveTab('availability')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'availability' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2340">
                Availability
              </button>}
            {user.permissions.includes('b2b-access') && <button onClick={() => setActiveTab('meetings')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'meetings' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2341">
                Meetings
              </button>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 py-8" data-id="element-2342">
        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="max-w-5xl mx-auto space-y-6" data-id="element-2343">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2344">
              <Card padding="lg" data-id="element-2345">
                <div className="flex items-center justify-between mb-4" data-id="element-2346">
                  <div data-id="element-2347">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2348">
                      Account Status
                    </h3>
                    <div className="text-2xl font-bold text-gray-900 mt-1 capitalize" data-id="element-2349">
                      {user.status}
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${user.status === 'active' ? 'bg-green-100' : user.status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'}`} data-id="element-2350">
                    <CheckCircle2Icon className={`w-6 h-6 ${user.status === 'active' ? 'text-green-600' : user.status === 'pending' ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-2351" />
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2352">
                  {user.status === 'active' ? 'Full access' : user.status === 'pending' ? 'Awaiting verification' : 'Access suspended'}
                </p>
              </Card>

              <Card padding="lg" data-id="element-2353">
                <div className="flex items-center justify-between mb-4" data-id="element-2354">
                  <div data-id="element-2355">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2356">
                      Member Since
                    </h3>
                    <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2357">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric'
                  })}
                    </div>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg" data-id="element-2358">
                    <CalendarIcon className="w-6 h-6 text-blue-600" data-id="element-2359" />
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2360">
                  {Math.floor((Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24))}{' '}
                  days ago
                </p>
              </Card>

              <Card padding="lg" data-id="element-2361">
                <div className="flex items-center justify-between mb-4" data-id="element-2362">
                  <div data-id="element-2363">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2364">
                      Last Login
                    </h3>
                    <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2365">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  }) : 'Never'}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg" data-id="element-2366">
                    <ClockIcon className="w-6 h-6 text-gray-600" data-id="element-2367" />
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2368">
                  {user.lastLogin ? `${Math.floor((Date.now() - new Date(user.lastLogin).getTime()) / (1000 * 60 * 60 * 24))} days ago` : 'No login recorded'}
                </p>
              </Card>

              <Card padding="lg" data-id="element-2369">
                <div className="flex items-center justify-between mb-4" data-id="element-2370">
                  <div data-id="element-2371">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2372">
                      Engagement
                    </h3>
                    <div className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2373">
                      High
                    </div>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-lg" data-id="element-2374">
                    <TrendingUpIcon className="w-6 h-6 text-purple-600" data-id="element-2375" />
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2376">Active user</p>
              </Card>
            </div>

            {/* User Information Card */}
            <Card padding="lg" data-id="element-2377">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2378">
                User Information
              </h3>
              <div className="grid grid-cols-2 gap-6" data-id="element-2379">
                <div data-id="element-2380">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2" data-id="element-2381">
                    Email
                  </label>
                  <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-2382">
                    <MailIcon className="w-4 h-4 text-gray-400" data-id="element-2383" />
                    {user.email}
                  </div>
                </div>
                <div data-id="element-2384">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2" data-id="element-2385">
                    Permissions
                  </label>
                  <div className="flex gap-1 flex-wrap" data-id="element-2386">
                    {user.permissions.map(permission => <Badge key={permission} variant={getPermissionBadgeVariant(permission)} size="sm" data-id="element-2387">
                        {getPermissionLabel(permission)}
                      </Badge>)}
                  </div>
                </div>
                {user.role && <div data-id="element-2388">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2" data-id="element-2389">
                      Role
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-2390">
                      <UserIcon className="w-4 h-4 text-gray-400" data-id="element-2391" />
                      {user.role}
                    </div>
                  </div>}
                {user.companyName && <div data-id="element-2392">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2" data-id="element-2393">
                      Company
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-2394">
                      <BuildingIcon className="w-4 h-4 text-gray-400" data-id="element-2395" />
                      {user.companyName}
                      {user.standNumber && <span className="text-gray-500" data-id="element-2396">
                          • Stand {user.standNumber}
                        </span>}
                    </div>
                  </div>}
                <div data-id="element-2397">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2" data-id="element-2398">
                    Registration Source
                  </label>
                  <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-2399">
                    <UserCheckIcon className="w-4 h-4 text-gray-400" data-id="element-2400" />
                    {getRegistrationSourceLabel()}
                  </div>
                </div>
              </div>
            </Card>

            {/* Permission Details */}
            <Card padding="lg" data-id="element-2401">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2402">
                Access & Permissions
              </h3>
              <div className="space-y-3" data-id="element-2403">
                {user.permissions.map(permission => <div key={permission} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg" data-id="element-2404">
                    <ShieldIcon className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-2405" />
                    <div className="flex-1" data-id="element-2406">
                      <div className="font-medium text-gray-900 mb-1" data-id="element-2407">
                        {getPermissionLabel(permission)}
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-2408">
                        {getPermissionDescription(permission)}
                      </div>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Speaker Engagements */}
            {user.permissions.includes('speaker') && <Card padding="lg" data-id="element-2409">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2410">
                  Speaker Engagements
                </h3>
                {speakerSessions.length > 0 ? <div className="space-y-3" data-id="element-2411">
                    {speakerSessions.map(session => <div key={session.id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer" data-id="element-2412">
                        {session.image && <img src={session.image} alt={session.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" data-id="element-2413" />}
                        <div className="flex-1 min-w-0" data-id="element-2414">
                          <h4 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-2415">
                            {session.title}
                          </h4>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mb-2" data-id="element-2416">
                            <div className="flex items-center gap-1" data-id="element-2417">
                              <CalendarIcon className="w-3 h-3" data-id="element-2418" />
                              {session.date}
                            </div>
                            <div className="flex items-center gap-1" data-id="element-2419">
                              <ClockIcon className="w-3 h-3" data-id="element-2420" />
                              {session.startTime} - {session.endTime}
                            </div>
                            <div className="flex items-center gap-1" data-id="element-2421">
                              <MapPinIcon className="w-3 h-3" data-id="element-2422" />
                              {session.location}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-2423">
                            <Badge variant="default" size="sm" data-id="element-2424">
                              {session.category}
                            </Badge>
                            <Badge variant="success" size="sm" data-id="element-2425">
                              {session.feature}
                            </Badge>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-8" data-id="element-2426">
                    <MicIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-2427" />
                    <p className="text-sm text-gray-600 font-medium mb-1" data-id="element-2428">
                      No agenda items confirmed yet
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2429">
                      Speaking sessions will appear here once they're added to
                      the agenda
                    </p>
                  </div>}
              </Card>}

            {/* Recent Activity */}
            <Card padding="lg" data-id="element-2430">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2431">
                Recent Activity
              </h3>
              <div className="space-y-3" data-id="element-2432">
                {mockRecentActivity.map(activity => <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0 last:pb-0" data-id="element-2433">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-2434">
                      <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-2435" />
                    </div>
                    <div className="flex-1" data-id="element-2436">
                      <p className="text-sm text-gray-900" data-id="element-2437">{activity.action}</p>
                      <div className="flex items-center gap-2 mt-1" data-id="element-2438">
                        <p className="text-xs text-gray-500" data-id="element-2439">{activity.time}</p>
                        <span className="text-gray-300" data-id="element-2440">•</span>
                        <p className="text-xs text-gray-500" data-id="element-2441">
                          {activity.device}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </Card>
          </div>}

        {/* User Profile Tab */}
        {activeTab === 'profile' && <div className="max-w-5xl mx-auto space-y-8" data-id="element-2442">
            {/* Personal Information */}
            <Card padding="lg" data-id="element-2443">
              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2444">
                Personal Information
              </h2>
              <div className="space-y-4" data-id="element-2445">
                <div className="grid grid-cols-2 gap-4" data-id="element-2446">
                  <div data-id="element-2447">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2448">
                      First Name
                    </label>
                    <Input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="John" data-id="element-2449" />
                  </div>
                  <div data-id="element-2450">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2451">
                      Last Name
                    </label>
                    <Input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Doe" data-id="element-2452" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-2453">
                  <div data-id="element-2454">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2455">
                      Email Address
                    </label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" data-id="element-2456" />
                  </div>
                  <div data-id="element-2457">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2458">
                      Phone Number
                    </label>
                    <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 50 123 4567" data-id="element-2459" />
                  </div>
                </div>

                <div data-id="element-2460">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2461">
                    Location
                  </label>
                  <Input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Dubai, UAE" data-id="element-2462" />
                </div>

                <div data-id="element-2463">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2464">
                    Bio
                  </label>
                  <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={500} rows={4} placeholder="Tell us about yourself..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-id="element-2465" />
                  <div className="text-xs text-gray-500 text-right mt-1" data-id="element-2466">
                    {bio.length} / 500 characters
                  </div>
                </div>
              </div>
            </Card>

            {/* Professional Information */}
            <Card padding="lg" data-id="element-2467">
              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2468">
                Professional Information
              </h2>
              <div className="space-y-4" data-id="element-2469">
                <div className="grid grid-cols-2 gap-4" data-id="element-2470">
                  <div data-id="element-2471">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2472">
                      Job Title
                    </label>
                    <Input type="text" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="Marketing Manager" data-id="element-2473" />
                  </div>
                  <div data-id="element-2474">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2475">
                      Company
                    </label>
                    <Input type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Corporation" data-id="element-2476" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-2477">
                  <div data-id="element-2478">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2479">
                      LinkedIn Profile
                    </label>
                    <Input type="url" value={linkedinUrl} onChange={e => setLinkedinUrl(e.target.value)} placeholder="https://linkedin.com/in/johndoe" data-id="element-2480" />
                  </div>
                  <div data-id="element-2481">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2482">
                      Website
                    </label>
                    <Input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://example.com" data-id="element-2483" />
                  </div>
                </div>
              </div>
            </Card>

            {/* Notification Preferences */}
            <Card padding="lg" data-id="element-2484">
              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2485">
                Notification Preferences
              </h2>
              <div className="space-y-4" data-id="element-2486">
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-2487">
                  <input type="checkbox" checked={emailNotifications} onChange={e => setEmailNotifications(e.target.checked)} className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2488" />
                  <div data-id="element-2489">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2490">
                      Email Notifications
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-2491">
                      Receive email updates about your account activity
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-2492">
                  <input type="checkbox" checked={pushNotifications} onChange={e => setPushNotifications(e.target.checked)} className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2493" />
                  <div data-id="element-2494">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2495">
                      Push Notifications
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-2496">
                      Receive push notifications on your mobile device
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-2497">
                  <input type="checkbox" checked={marketingEmails} onChange={e => setMarketingEmails(e.target.checked)} className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2498" />
                  <div data-id="element-2499">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2500">
                      Marketing Emails
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-2501">
                      Receive promotional emails and event updates
                    </div>
                  </div>
                </label>
              </div>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end" data-id="element-2502">
              <Button variant="primary" onClick={handleSaveProfile} data-id="element-2503">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-2504" />
                Save Changes
              </Button>
            </div>
          </div>}

        {/* Speaker Profile Tab */}
        {activeTab === 'speaker-profile' && <div className="max-w-5xl mx-auto space-y-8" data-id="element-2505">
            <Card padding="lg" data-id="element-2506">
              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2507">
                Speaker Information
              </h2>
              <div className="space-y-4" data-id="element-2508">
                <div data-id="element-2509">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2510">
                    Speaker Bio
                  </label>
                  <textarea placeholder="Share your expertise, background, and what makes you a great speaker..." maxLength={1000} rows={6} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-id="element-2511" />
                  <div className="text-xs text-gray-500 text-right mt-1" data-id="element-2512">
                    0 / 1000 characters
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-2513">
                  <div data-id="element-2514">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2515">
                      Expertise Areas
                    </label>
                    <Input type="text" placeholder="e.g., Fitness, Nutrition, Wellness" data-id="element-2516" />
                  </div>
                  <div data-id="element-2517">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2518">
                      Years of Experience
                    </label>
                    <Input type="number" placeholder="e.g., 10" data-id="element-2519" />
                  </div>
                </div>

                <div data-id="element-2520">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2521">
                    Previous Speaking Engagements
                  </label>
                  <textarea placeholder="List notable conferences, events, or presentations..." rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-id="element-2522" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-2523">
                  <div data-id="element-2524">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2525">
                      Social Media / Website
                    </label>
                    <Input type="url" placeholder="https://..." data-id="element-2526" />
                  </div>
                  <div data-id="element-2527">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2528">
                      Video Portfolio Link
                    </label>
                    <Input type="url" placeholder="YouTube, Vimeo, etc." data-id="element-2529" />
                  </div>
                </div>
              </div>
            </Card>

            <Card padding="lg" data-id="element-2530">
              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2531">
                Session Preferences
              </h2>
              <div className="space-y-4" data-id="element-2532">
                <div data-id="element-2533">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2534">
                    Preferred Session Types
                  </label>
                  <div className="space-y-2" data-id="element-2535">
                    <label className="flex items-center gap-2" data-id="element-2536">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2537" />
                      <span className="text-sm text-gray-700" data-id="element-2538">
                        Keynote Presentations
                      </span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2539">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2540" />
                      <span className="text-sm text-gray-700" data-id="element-2541">
                        Panel Discussions
                      </span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2542">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2543" />
                      <span className="text-sm text-gray-700" data-id="element-2544">Workshops</span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2545">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2546" />
                      <span className="text-sm text-gray-700" data-id="element-2547">
                        Q&A Sessions
                      </span>
                    </label>
                  </div>
                </div>

                <div data-id="element-2548">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2549">
                    Special Requirements
                  </label>
                  <textarea placeholder="Any technical requirements, accessibility needs, or special requests..." rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-id="element-2550" />
                </div>
              </div>
            </Card>

            <div className="flex justify-end" data-id="element-2551">
              <Button variant="primary" data-id="element-2552">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-2553" />
                Save Speaker Profile
              </Button>
            </div>
          </div>}

        {/* Buyer Profile Tab */}
        {activeTab === 'buyer-profile' && <div className="max-w-5xl mx-auto space-y-8" data-id="element-2554">
            <Card padding="lg" data-id="element-2555">
              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2556">
                Buyer Information
              </h2>
              <div className="space-y-4" data-id="element-2557">
                <div className="grid grid-cols-2 gap-4" data-id="element-2558">
                  <div data-id="element-2559">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2560">
                      Company Type
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2561">
                      <option value="" data-id="element-2562">Select type...</option>
                      <option value="gym" data-id="element-2563">Gym / Fitness Center</option>
                      <option value="retailer" data-id="element-2564">Retailer</option>
                      <option value="distributor" data-id="element-2565">Distributor</option>
                      <option value="manufacturer" data-id="element-2566">Manufacturer</option>
                      <option value="other" data-id="element-2567">Other</option>
                    </select>
                  </div>
                  <div data-id="element-2568">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2569">
                      Company Size
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2570">
                      <option value="" data-id="element-2571">Select size...</option>
                      <option value="1-10" data-id="element-2572">1-10 employees</option>
                      <option value="11-50" data-id="element-2573">11-50 employees</option>
                      <option value="51-200" data-id="element-2574">51-200 employees</option>
                      <option value="201-500" data-id="element-2575">201-500 employees</option>
                      <option value="500+" data-id="element-2576">500+ employees</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-2577">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2578">
                    Purchasing Authority
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2579">
                    <option value="" data-id="element-2580">Select authority level...</option>
                    <option value="decision-maker" data-id="element-2581">Final Decision Maker</option>
                    <option value="influencer" data-id="element-2582">Influencer</option>
                    <option value="recommender" data-id="element-2583">Recommender</option>
                    <option value="researcher" data-id="element-2584">Researcher</option>
                  </select>
                </div>

                <div data-id="element-2585">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2586">
                    Product Categories of Interest
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-id="element-2587">
                    <label className="flex items-center gap-2" data-id="element-2588">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2589" />
                      <span className="text-sm text-gray-700" data-id="element-2590">
                        Fitness Equipment
                      </span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2591">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2592" />
                      <span className="text-sm text-gray-700" data-id="element-2593">
                        Nutrition & Supplements
                      </span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2594">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2595" />
                      <span className="text-sm text-gray-700" data-id="element-2596">
                        Apparel & Accessories
                      </span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2597">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2598" />
                      <span className="text-sm text-gray-700" data-id="element-2599">
                        Technology & Software
                      </span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2600">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2601" />
                      <span className="text-sm text-gray-700" data-id="element-2602">Services</span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2603">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2604" />
                      <span className="text-sm text-gray-700" data-id="element-2605">
                        Wellness Products
                      </span>
                    </label>
                  </div>
                </div>

                <div data-id="element-2606">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2607">
                    Annual Budget Range
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2608">
                    <option value="" data-id="element-2609">Select budget range...</option>
                    <option value="under-50k" data-id="element-2610">Under $50,000</option>
                    <option value="50k-100k" data-id="element-2611">$50,000 - $100,000</option>
                    <option value="100k-250k" data-id="element-2612">$100,000 - $250,000</option>
                    <option value="250k-500k" data-id="element-2613">$250,000 - $500,000</option>
                    <option value="500k-1m" data-id="element-2614">$500,000 - $1,000,000</option>
                    <option value="1m+" data-id="element-2615">$1,000,000+</option>
                  </select>
                </div>

                <div data-id="element-2616">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2617">
                    Purchasing Timeline
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2618">
                    <option value="" data-id="element-2619">Select timeline...</option>
                    <option value="immediate" data-id="element-2620">Immediate (0-3 months)</option>
                    <option value="short-term" data-id="element-2621">Short-term (3-6 months)</option>
                    <option value="medium-term" data-id="element-2622">
                      Medium-term (6-12 months)
                    </option>
                    <option value="long-term" data-id="element-2623">Long-term (12+ months)</option>
                  </select>
                </div>
              </div>
            </Card>

            <Card padding="lg" data-id="element-2624">
              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2625">
                Sourcing Preferences
              </h2>
              <div className="space-y-4" data-id="element-2626">
                <div data-id="element-2627">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2628">
                    Preferred Supplier Regions
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-id="element-2629">
                    <label className="flex items-center gap-2" data-id="element-2630">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2631" />
                      <span className="text-sm text-gray-700" data-id="element-2632">Middle East</span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2633">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2634" />
                      <span className="text-sm text-gray-700" data-id="element-2635">Europe</span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2636">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2637" />
                      <span className="text-sm text-gray-700" data-id="element-2638">
                        North America
                      </span>
                    </label>
                    <label className="flex items-center gap-2" data-id="element-2639">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2640" />
                      <span className="text-sm text-gray-700" data-id="element-2641">
                        Asia Pacific
                      </span>
                    </label>
                  </div>
                </div>

                <div data-id="element-2642">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2643">
                    Special Requirements or Notes
                  </label>
                  <textarea placeholder="Any specific requirements, certifications needed, or additional information..." rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-id="element-2644" />
                </div>
              </div>
            </Card>

            <div className="flex justify-end" data-id="element-2645">
              <Button variant="primary" data-id="element-2646">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-2647" />
                Save Buyer Profile
              </Button>
            </div>
          </div>}

        {/* Meetings Tab */}
        {activeTab === 'meetings' && <div className="max-w-5xl mx-auto space-y-6" data-id="element-2648">
            {/* Date Selector */}
            <div data-id="element-2649">
              <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-2650">
                Select Date
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-2651">
                {eventDates.map(date => <button key={date} onClick={() => {
              setSelectedMeetingDate(date);
              setSelectedMeetingSlot(null);
            }} className={`p-4 rounded-lg border-2 text-sm font-medium transition-all ${selectedMeetingDate === date ? 'border-blue-600 bg-blue-50 text-blue-600 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2652">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-2653">
                      {new Date(date).toLocaleDateString('en-US', {
                  weekday: 'short'
                })}
                    </div>
                    <div className="font-bold" data-id="element-2654">
                      {new Date(date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}
                    </div>
                  </button>)}
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2655">
              <Card padding="lg" className="lg:col-span-2" data-id="element-2656">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2657">
                  {new Date(selectedMeetingDate).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
                </h3>

                <div className="grid grid-cols-4 gap-2" data-id="element-2658">
                  {timeSlots.map(slot => {
                const meeting = getSlotMeeting(selectedMeetingDate, slot);
                const isAvailable = isSlotAvailable(selectedMeetingDate, slot);
                if (meeting) {
                  return <button key={slot} onClick={() => {
                    setSelectedMeetingSlot(slot);
                    setSelectedMeetingDetails(meeting);
                  }} className={`p-3 rounded-lg text-sm font-medium transition-all ${selectedMeetingSlot === slot ? 'ring-2 ring-offset-2 ring-blue-600' : ''} ${meeting.status === 'confirmed' ? 'bg-green-100 text-green-700 border-2 border-green-300 hover:bg-green-200' : 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300 hover:bg-yellow-200'}`} data-id="element-2659">
                          {slot}
                        </button>;
                }
                return <button key={slot} onClick={() => {
                  setSelectedMeetingSlot(slot);
                  setSelectedMeetingDetails(null);
                }} className={`p-3 rounded-lg text-sm font-medium transition-all ${selectedMeetingSlot === slot ? 'ring-2 ring-blue-600 ring-offset-2 bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'}`} data-id="element-2660">
                        {slot}
                      </button>;
              })}
                </div>

                <div className="mt-4 flex items-center gap-4 text-xs" data-id="element-2661">
                  <div className="flex items-center gap-2" data-id="element-2662">
                    <div className="w-3 h-3 rounded bg-green-200" data-id="element-2663"></div>
                    <span data-id="element-2664">Confirmed</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2665">
                    <div className="w-3 h-3 rounded bg-yellow-200" data-id="element-2666"></div>
                    <span data-id="element-2667">Pending</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-2668">
                    <div className="w-3 h-3 rounded bg-gray-200" data-id="element-2669"></div>
                    <span data-id="element-2670">Available</span>
                  </div>
                </div>
              </Card>

              {/* Meeting Details Panel */}
              <Card padding="lg" data-id="element-2671">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2672">
                  {selectedMeetingDetails ? 'Meeting Details' : 'Time Slot'}
                </h3>

                {selectedMeetingDetails ? <div className="space-y-4" data-id="element-2673">
                    <div className="flex items-center justify-between" data-id="element-2674">
                      <Badge variant={selectedMeetingDetails.status === 'confirmed' ? 'success' : 'warning'} size="md" data-id="element-2675">
                        {selectedMeetingDetails.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-3" data-id="element-2676">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold flex-shrink-0" data-id="element-2677">
                        {selectedMeetingDetails.attendeeName.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div data-id="element-2678">
                        <h4 className="font-bold text-gray-900" data-id="element-2679">
                          {selectedMeetingDetails.attendeeName}
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-2680">
                          {selectedMeetingDetails.attendeeRole}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-gray-200" data-id="element-2681">
                      <div className="flex items-start gap-3" data-id="element-2682">
                        <BuildingIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2683" />
                        <div data-id="element-2684">
                          <p className="text-xs text-gray-500" data-id="element-2685">Company</p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-2686">
                            {selectedMeetingDetails.attendeeCompany}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-2687">
                        <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2688" />
                        <div data-id="element-2689">
                          <p className="text-xs text-gray-500" data-id="element-2690">Date</p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-2691">
                            {new Date(selectedMeetingDetails.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                      })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-2692">
                        <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2693" />
                        <div data-id="element-2694">
                          <p className="text-xs text-gray-500" data-id="element-2695">Time</p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-2696">
                            {selectedMeetingDetails.startTime} -{' '}
                            {selectedMeetingDetails.endTime}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3" data-id="element-2697">
                        <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2698" />
                        <div data-id="element-2699">
                          <p className="text-xs text-gray-500" data-id="element-2700">Location</p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-2701">
                            {selectedMeetingDetails.location}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 space-y-2" data-id="element-2702">
                      {selectedMeetingDetails.status === 'confirmed' && <Button variant="outline" fullWidth data-id="element-2703">
                          <XIcon className="w-4 h-4 mr-2" data-id="element-2704" />
                          Cancel Meeting
                        </Button>}
                      {selectedMeetingDetails.status === 'pending' && <>
                          <Button variant="primary" fullWidth data-id="element-2705">
                            <CheckIcon className="w-4 h-4 mr-2" data-id="element-2706" />
                            Accept
                          </Button>
                          <Button variant="outline" fullWidth data-id="element-2707">
                            <XIcon className="w-4 h-4 mr-2" data-id="element-2708" />
                            Decline
                          </Button>
                        </>}
                    </div>
                  </div> : selectedMeetingSlot ? <div className="space-y-4" data-id="element-2709">
                    <div className="flex items-center gap-3" data-id="element-2710">
                      <ClockIcon className="w-5 h-5 text-gray-400" data-id="element-2711" />
                      <div data-id="element-2712">
                        <p className="text-xs text-gray-500" data-id="element-2713">Selected Time</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-2714">
                          {selectedMeetingSlot}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3" data-id="element-2715">
                      <CalendarIcon className="w-5 h-5 text-gray-400" data-id="element-2716" />
                      <div data-id="element-2717">
                        <p className="text-xs text-gray-500" data-id="element-2718">Date</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-2719">
                          {new Date(selectedMeetingDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric'
                    })}
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200" data-id="element-2720">
                      <p className="text-sm text-gray-600 mb-3" data-id="element-2721">
                        This time slot is available. You can schedule a meeting
                        here.
                      </p>
                      <Button variant="primary" fullWidth data-id="element-2722">
                        <CalendarIcon className="w-4 h-4 mr-2" data-id="element-2723" />
                        Schedule Meeting
                      </Button>
                    </div>
                  </div> : <div className="text-center py-8" data-id="element-2724">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-2725" />
                    <p className="text-sm text-gray-500" data-id="element-2726">
                      Select a time slot to view details
                    </p>
                  </div>}
              </Card>
            </div>

            {/* Meetings List */}
            {getMeetingsForDate(selectedMeetingDate).length > 0 && <Card padding="lg" data-id="element-2727">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2728">
                  Scheduled Meetings
                </h3>
                <div className="space-y-3" data-id="element-2729">
                  {getMeetingsForDate(selectedMeetingDate).map(meeting => <div key={meeting.id} onClick={() => {
              setSelectedMeetingSlot(meeting.startTime);
              setSelectedMeetingDetails(meeting);
            }} className={`flex items-start gap-4 p-4 border-2 rounded-lg transition-colors cursor-pointer ${selectedMeetingDetails?.id === meeting.id ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`} data-id="element-2730">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold flex-shrink-0" data-id="element-2731">
                        {meeting.attendeeName.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2732">
                        <div className="flex items-start justify-between gap-2 mb-1" data-id="element-2733">
                          <h4 className="font-semibold text-gray-900" data-id="element-2734">
                            {meeting.attendeeName}
                          </h4>
                          <Badge variant={meeting.status === 'confirmed' ? 'success' : 'warning'} size="sm" data-id="element-2735">
                            {meeting.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2" data-id="element-2736">
                          {meeting.attendeeRole} at {meeting.attendeeCompany}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-gray-500" data-id="element-2737">
                          <div className="flex items-center gap-1" data-id="element-2738">
                            <ClockIcon className="w-3 h-3" data-id="element-2739" />
                            {meeting.startTime} - {meeting.endTime}
                          </div>
                          <div className="flex items-center gap-1" data-id="element-2740">
                            <MapPinIcon className="w-3 h-3" data-id="element-2741" />
                            {meeting.location}
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </Card>}
          </div>}

        {/* Availability Tab */}
        {activeTab === 'availability' && <div className="max-w-5xl mx-auto space-y-6" data-id="element-2742">
            {/* Meeting Preferences */}
            <Card padding="lg" data-id="element-2743">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2744">
                Meeting Preferences
              </h3>
              <div className="space-y-4" data-id="element-2745">
                <div data-id="element-2746">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-2747">
                    How would you like attendees to connect with you?
                  </label>
                  <div className="space-y-3" data-id="element-2748">
                    <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-blue-300 hover:bg-blue-50" data-id="element-2749">
                      <input type="radio" name="meetingPreference" value="direct" checked={meetingPreference === 'direct'} onChange={e => setMeetingPreference(e.target.value as 'direct' | 'chat-first')} className="mt-1" data-id="element-2750" />
                      <div className="flex-1" data-id="element-2751">
                        <div className="font-medium text-gray-900 mb-1" data-id="element-2752">
                          Allow direct meeting requests
                        </div>
                        <div className="text-sm text-gray-600" data-id="element-2753">
                          Attendees can request meetings directly without
                          chatting first. You'll receive meeting requests based
                          on your availability.
                        </div>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-blue-300 hover:bg-blue-50" data-id="element-2754">
                      <input type="radio" name="meetingPreference" value="chat-first" checked={meetingPreference === 'chat-first'} onChange={e => setMeetingPreference(e.target.value as 'direct' | 'chat-first')} className="mt-1" data-id="element-2755" />
                      <div className="flex-1" data-id="element-2756">
                        <div className="font-medium text-gray-900 mb-1" data-id="element-2757">
                          Require chat before meeting
                        </div>
                        <div className="text-sm text-gray-600" data-id="element-2758">
                          Attendees must start a conversation with you before
                          requesting a meeting. This helps you screen
                          connections.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </Card>

            {/* Availability Schedule */}
            <Card padding="lg" data-id="element-2759">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2760">
                Availability Schedule
              </h3>

              {/* Date Selector */}
              <div className="mb-6" data-id="element-2761">
                <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-2762">
                  Select Date
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-2763">
                  {eventDates.map(date => <button key={date} onClick={() => setSelectedAvailabilityDate(date)} className={`p-4 rounded-lg border-2 text-sm font-medium transition-all ${selectedAvailabilityDate === date ? 'border-blue-600 bg-blue-50 text-blue-600 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2764">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-2765">
                        {new Date(date).toLocaleDateString('en-US', {
                    weekday: 'short'
                  })}
                      </div>
                      <div className="font-bold" data-id="element-2766">
                        {new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                      </div>
                    </button>)}
                </div>
              </div>

              {/* Time Slot Selection */}
              <div data-id="element-2767">
                <div className="flex items-center justify-between mb-3" data-id="element-2768">
                  <label className="text-sm font-semibold text-gray-700" data-id="element-2769">
                    Available Time Slots for{' '}
                    {new Date(selectedAvailabilityDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric'
                })}
                  </label>
                  <div className="flex gap-2" data-id="element-2770">
                    <button onClick={() => {
                  setAvailabilityByDate({
                    ...availabilityByDate,
                    [selectedAvailabilityDate]: timeSlots
                  });
                }} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-2771">
                      Select All
                    </button>
                    <button onClick={() => {
                  setAvailabilityByDate({
                    ...availabilityByDate,
                    [selectedAvailabilityDate]: []
                  });
                }} className="text-sm text-gray-600 hover:text-gray-700 font-medium" data-id="element-2772">
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2" data-id="element-2773">
                  {timeSlots.map(slot => {
                const isSelected = availabilityByDate[selectedAvailabilityDate]?.includes(slot) || false;
                return <button key={slot} onClick={() => {
                  const currentSlots = availabilityByDate[selectedAvailabilityDate] || [];
                  const newSlots = isSelected ? currentSlots.filter(s => s !== slot) : [...currentSlots, slot];
                  setAvailabilityByDate({
                    ...availabilityByDate,
                    [selectedAvailabilityDate]: newSlots
                  });
                }} className={`p-3 rounded-lg text-sm font-medium transition-all ${isSelected ? 'bg-blue-600 text-white border-2 border-blue-600 hover:bg-blue-700' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'}`} data-id="element-2774">
                        {slot}
                      </button>;
              })}
                </div>

                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-2775">
                  <div className="flex items-start gap-2" data-id="element-2776">
                    <CalendarIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-2777" />
                    <div className="text-sm text-blue-900" data-id="element-2778">
                      <strong data-id="element-2779">
                        {availabilityByDate[selectedAvailabilityDate]?.length || 0}{' '}
                        time slots
                      </strong>{' '}
                      selected for{' '}
                      {new Date(selectedAvailabilityDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end" data-id="element-2780">
              <Button variant="primary" data-id="element-2781">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-2782" />
                Save Availability Settings
              </Button>
            </div>
          </div>}
      </div>
    </div>;
}